<template>
  <v-navigation-drawer
    :value="value"
    right
    temporary
    app
    width="420"
    color="background"
    :dark="$vuetify.theme.dark"
    @input="$emit('input', $event)"
  >
    <div class="pa-4 pb-2">
      <div class="d-flex align-center justify-space-between mb-2">
        <h2 class="text-h6 mb-0">Notifications</h2>
        <div class="d-flex align-center">
          <v-btn
            v-if="notificationsStore.unreadCount"
            text
            x-small
            color="primary"
            class="mr-1"
            @click="notificationsStore.markAllRead()"
          >
            Tout marquer lu
          </v-btn>
          <v-btn
            v-if="$vuetify.breakpoint.smAndDown"
            icon
            small
            @click="$emit('input', false)"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>
      </div>
    </div>

    <v-tabs
      :value="tabIndex"
      background-color="transparent"
      color="primary"
      grow
      @change="onTab"
    >
      <v-tab>
        <v-badge
          :content="unread.company"
          :value="unread.company > 0"
          color="info"
        >
          Entreprise
        </v-badge>
      </v-tab>
      <v-tab>
        <v-badge
          :content="unread.account"
          :value="unread.account > 0"
          color="info"
        >
          Compte
        </v-badge>
      </v-tab>
      <v-tab>
        <v-badge
          :content="unread.team"
          :value="unread.team > 0"
          color="info"
        >
          Katana
        </v-badge>
      </v-tab>
    </v-tabs>
    <v-divider />

    <div v-if="notificationsStore.loadStatus === 'loading'" class="pa-6 text-center">
      <v-progress-circular indeterminate color="primary" />
    </div>

    <div
      v-else-if="!notificationsStore.filtered.length"
      class="pa-6 text-center grey--text"
    >
      Aucune notification
    </div>

    <div v-else class="notifications-list">
      <template v-for="section in sections">
        <p
          v-if="section.items.length"
          :key="`${section.key}-label`"
          class="overline px-4 pt-4 mb-1"
        >
          {{ section.label }}
        </p>
        <v-list
          v-if="section.items.length"
          :key="section.key"
          class="py-0"
        >
          <v-list-item
            v-for="item in section.items"
            :key="item.storeKey || item.id"
            class="notification-item"
            @click="openItem(item)"
          >
            <v-list-item-avatar class="notification-avatar-wrap">
              <v-tooltip left color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <div
                    class="notification-avatar"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-avatar size="40" class="notification-avatar__brand">
                      <img
                        :src="$appConfig.appLogoUrl"
                        alt="Katana Intelligence"
                        class="notification-avatar__logo"
                      >
                    </v-avatar>
                    <v-avatar
                      v-if="hasHumanActor(item)"
                      size="20"
                      color="info"
                      class="notification-avatar__actor white--text"
                    >
                      <img
                        v-if="item.actor.avatar"
                        :src="$functions.getImgLink(item.actor.avatar, 50)"
                        :alt="actorLabel(item)"
                      >
                      <span v-else class="caption">{{ item.actorInitials }}</span>
                    </v-avatar>
                  </div>
                </template>
                <span>{{ actorLabel(item) }}</span>
              </v-tooltip>
            </v-list-item-avatar>
            <v-list-item-content class="notification-item__content">
              <v-list-item-title class="text-wrap">
                {{ item.title }}
                <span class="caption grey--text ml-1">{{ relative(item.createdAt) }}</span>
              </v-list-item-title>
              <v-list-item-subtitle v-if="item.entity" class="text-wrap">
                {{ item.entity.label }}
              </v-list-item-subtitle>
              <v-list-item-subtitle
                v-else-if="item.body"
                class="text-wrap notification-body"
                v-html="safeBody(item.body)"
              />
              <div v-if="item.entity" class="caption mt-1">
                {{ item.entity.id }}
                <span v-if="item.entity.status"> • {{ item.entity.status }}</span>
              </div>
            </v-list-item-content>
            <v-list-item-action class="notification-actions">
              <v-tooltip v-if="item.link" left color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="openLink(item)"
                  >
                    <v-icon small>mdi-open-in-new</v-icon>
                  </v-btn>
                </template>
                <span>{{ item.link }}</span>
              </v-tooltip>
              <v-tooltip left color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    :disabled="item.read"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="markRead(item)"
                  >
                    <v-icon small>
                      {{ item.read ? "mdi-email-open-outline" : "mdi-email-outline" }}
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ item.read ? "Déjà lu" : "Marquer comme lu" }}</span>
              </v-tooltip>
              <v-tooltip v-if="adminAccess" left color="primary">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    color="error"
                    v-bind="attrs"
                    v-on="on"
                    @click.stop="removeItem(item)"
                  >
                    <v-icon small>mdi-delete-outline</v-icon>
                  </v-btn>
                </template>
                <span>Supprimer</span>
              </v-tooltip>
              <v-icon v-if="!item.read" small color="primary">mdi-circle</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </template>

      <div v-if="notificationsStore.hasMoreRead" class="pa-4 text-center">
        <v-btn
          text
          small
          color="primary"
          @click="notificationsStore.loadMoreRead()"
        >
          Charger plus
        </v-btn>
      </div>
    </div>
  </v-navigation-drawer>
</template>

<script>
import { setSnackbar } from "@/stores/ui";
import { mapStores } from "pinia";
import { useNotificationsStore } from "@/stores/notifications";
import { sanitizeNotificationHtml } from "@/utils/sanitizeNotificationHtml";

const TAB_CHANNELS = ["company", "account", "team"];

export default {
  name: "NotificationsDrawer",

  props: {
    value: {
      type: Boolean,
      default: false,
    },
    adminAccess: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    ...mapStores(useNotificationsStore),

    tabIndex() {
      return Math.max(0, TAB_CHANNELS.indexOf(this.notificationsStore.channel));
    },

    unread() {
      return this.notificationsStore.unreadByChannel;
    },

    sections() {
      return [
        { key: "today", label: "Aujourd'hui", items: this.notificationsStore.grouped.today },
        { key: "earlier", label: "Plus tôt", items: this.notificationsStore.grouped.earlier },
      ];
    },
  },

  methods: {
    onTab(index) {
      this.notificationsStore.setChannel(TAB_CHANNELS[index] || "company");
    },

    hasHumanActor(item) {
      const actor = item?.actor;
      if (!actor) return false;
      if (actor.system === true) return false;
      if (!actor.id) return false;
      const name = String(actor.displayName || "").trim().toLowerCase();
      return name && name !== "katana" && name !== "katana intelligence";
    },

    actorLabel(item) {
      if (this.hasHumanActor(item)) {
        const name = String(item.actor.displayName || "").trim();
        return `${name} · Katana Intelligence`;
      }
      return "Katana Intelligence";
    },

    safeBody(body) {
      return sanitizeNotificationHtml(body);
    },

    relative(value) {
      const ms = (() => {
        if (!value) return 0;
        if (typeof value.toDate === "function") return value.toDate().getTime();
        if (value._seconds != null) return value._seconds * 1000;
        if (value.seconds != null) return value.seconds * 1000;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
      })();
      if (!ms) return "";
      const delta = Date.now() - ms;
      if (delta < 60000) return "à l'instant";
      if (delta < 3600000) return `il y a ${Math.floor(delta / 60000)} min`;
      if (delta < 86400000) return `il y a ${Math.floor(delta / 3600000)} h`;
      return this.$functions.getDateFormat(value);
    },

    async markRead(item) {
      if (!item.read) await this.notificationsStore.markRead(item.storeKey || item.id);
    },

    async removeItem(item) {
      if (!this.adminAccess) return;
      try {
        await this.notificationsStore.remove(item.storeKey || item.id);
      } catch (error) {
        console.error(error);
        setSnackbar({
          text: "Impossible de supprimer la notification",
          color: "error",
        });
      }
    },

    openLink(item) {
      if (!item.link) return;
      if (/^https?:\/\//i.test(item.link)) {
        window.open(item.link, "_blank", "noopener,noreferrer");
        return;
      }
      this.$router.push(item.link).catch(() => undefined);
    },

    async openItem(item) {
      if (!item.read) await this.notificationsStore.markRead(item.storeKey || item.id);
    },
  },
};
</script>

<style scoped>
.notifications-list {
  max-height: calc(100vh - 140px);
  overflow-y: auto;
}

.notification-item {
  align-items: center !important;
  min-height: 64px;
}

.notification-item__content {
  align-self: center !important;
  padding-top: 10px !important;
  padding-bottom: 10px !important;
}

.notification-actions {
  align-self: center !important;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2px;
  margin: 0 !important;
}

.notification-body >>> a {
  color: inherit;
  text-decoration: underline;
}

.notification-avatar-wrap {
  overflow: visible !important;
  align-self: center !important;
  margin-top: 0 !important;
  margin-bottom: 0 !important;
}

.notification-avatar {
  position: relative;
  width: 40px;
  height: 40px;
}

.notification-avatar__logo {
  width: 70%;
  height: 70%;
  object-fit: contain;
}

.notification-avatar__actor {
  position: absolute;
  right: -2px;
  bottom: -2px;
  border: 2px solid var(--v-background-base, #1e1e1e);
  font-size: 9px;
  z-index: 1;
}
</style>
